import { createContext, useContext, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import {
  NegotiationRoundStatus,
  useListNegotiationRoundsV2Query,
  type NegotiationRoundV2Dto,
  type SourcingEventV2Dto,
} from "../../../../autogen/bff-api";
import { supportsNegotiationRounds } from "./supportsNegotiationRounds";

const ORIGINAL_COMPETITION = "original";

interface RoundSelection {
  rounds: NegotiationRoundV2Dto[];
  /** null = the original competition (round 0). */
  selectedRound: NegotiationRoundV2Dto | null;
  latestRound: NegotiationRoundV2Dto | null;
  selectRound: (round: NegotiationRoundV2Dto | null) => void;
}

const NO_ROUNDS: RoundSelection = {
  rounds: [],
  selectedRound: null,
  latestRound: null,
  selectRound: () => undefined,
};

const RoundSelectionContext = createContext<RoundSelection>(NO_ROUNDS);

/**
 * ADR-033 D1: round selection is view-level context. The `?round=` param holds a round id or
 * "original"; absent means the latest round — the buyer lands where the competition stands.
 */
export const RoundSelectionProvider = ({
  event,
  children,
}: {
  event: SourcingEventV2Dto;
  children: ReactNode;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data } = useListNegotiationRoundsV2Query(
    supportsNegotiationRounds(event) ? { eventId: event.id } : skipToken,
  );
  const rounds = [...(data?.rounds ?? [])].sort((a, b) => a.roundNumber - b.roundNumber);
  const latestRound = rounds.length > 0 ? rounds[rounds.length - 1] : null;

  const param = searchParams.get("round");
  const selectedRound =
    param === ORIGINAL_COMPETITION ? null : rounds.find((round) => round.id === param) ?? latestRound;

  const selectRound = (round: NegotiationRoundV2Dto | null) => {
    const next = new URLSearchParams(searchParams);
    next.set("round", round?.id ?? ORIGINAL_COMPETITION);
    setSearchParams(next, { replace: true });
  };

  return (
    <RoundSelectionContext.Provider value={{ rounds, selectedRound, latestRound, selectRound }}>
      {children}
    </RoundSelectionContext.Provider>
  );
};

export const useRoundSelection = (): RoundSelection => useContext(RoundSelectionContext);

/**
 * ADR-033 D2: the award works on the effective final stage — the latest COMPLETED round
 * when rounds exist, else the original competition (null). Pinned, never pager-driven.
 */
export const useFinalStageRound = (): NegotiationRoundV2Dto | null => {
  const { rounds } = useRoundSelection();
  const completed = rounds.filter((round) => round.status === NegotiationRoundStatus.Completed);
  return completed[completed.length - 1] ?? null;
};
